import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@picklebet/ui";
import { PasswordInput } from "@picklebet/ui";
import { Button } from "@picklebet/ui";
import { Form } from "@picklebet/ui";
import { signIn } from "../services/session";
import { hasFieldErrors } from "../utils/authFetch";
import { Errors } from "./Errors";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { Typography } from "@picklebet/ui";
import { useShellStore } from "@/features/shell/stores/shellStore";
import type { ILastSession } from "@/utilities/lastSession";
import { Avatar } from "@picklebet/ui";
import { navigate } from "@/utilities/navigate";
import { usePublicProfile } from "@/hooks/firestore/user/usePublicProfile";
import { PROFILE_COMPLETION_MODAL } from "./ProfileCompletionModal";
import { PageLoader } from "@picklebet/ui";
import { telemetry } from "@/utilities/telemetry";
import { doc, getDoc } from "firebase/firestore";
import { getFirestore } from "utilities/firebase";
import { user } from "@/hooks/firestore/models/user";

import * as styles from "./Auth.module.scss";

const schema = z.object({
  email: z.string().min(1, "Email is required"),
  password: z.string().min(1, "Password is required"),
});

type FormValues = z.infer<typeof schema>;

type SignInFormProps = {
  onComplete?: () => void;
  onFail?: () => void;
  onRequiresReopen?: () => void;
  lastSession?: ILastSession;
  onIgnoreLastSession?: () => void;
};

const getUserOnce = async (userId: string) => {
  const snapshot = await getDoc(doc(getFirestore(), "users", userId));
  if (!snapshot.exists()) return null;

  const parsed = user.safeParse(snapshot.data());

  return parsed.data ?? snapshot.data();
};

const SignInForm = ({
  onComplete,
  lastSession,
  onIgnoreLastSession,
}: SignInFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: lastSession?.email ?? "",
    },
  });
  const setModal = useShellStore((state) => state.setModal);
  const isDeeplinkOverlayActive = useShellStore(
    (state) => state.isDeeplinkOverlayActive,
  );
  const isBiometricProcessing = useShellStore(
    (state) => state.isBiometricProcessing,
  );

  const { profile: publicProfile, isLoading } = usePublicProfile(
    lastSession?.uid,
  );
  const shouldUseLastSession = !!lastSession && publicProfile;

  useEffect(() => {
    if (!lastSession) {
      reset({ email: "" });
    }
  }, [lastSession, reset]);

  if (isLoading) {
    return <PageLoader />;
  }
  return (
    <>
      <Form
        onSubmit={handleSubmit(async ({ email, password }: FormValues) => {
          const result = await signIn({ email, password });

          if (result.ok) {
            const user = await getUserOnce(result.data.userId);
            const isMissingPhone = !user?.profile?.phone;

            setModal(null);

            const permissions = result.data.permissions || {};

            if (permissions?.reopenAccount === "GRANTED") {
              navigate("/account/reopen");
            }

            if (
              permissions?.submitBet === "INCOMPLETE_PROFILE" ||
              (isMissingPhone && user?.profile?.country === "AU")
            ) {
              setModal(PROFILE_COMPLETION_MODAL);
            }

            if (onComplete) onComplete();
          } else if (hasFieldErrors(result.error)) {
            const fieldErrors = result.error.errors;

            Object.keys(result.error.errors).forEach((key, index) => {
              setError(
                key as keyof FormValues,
                {
                  type: "manual",
                  message: fieldErrors[key],
                },
                // should focus on first error message
                { shouldFocus: index === 0 },
              );
            });
          } else {
            const { message, messageTemplate } = result.error;

            setError("root.server", {
              type: messageTemplate,
              message,
            });
          }
        })}
      >
        {shouldUseLastSession ? (
          <div className={styles.lastSession}>
            <Avatar
              className={styles.avatar}
              src={publicProfile?.avatarUri}
              alt={`${publicProfile?.firstName}'s Avatar`}
              size={120}
              name={publicProfile?.username}
            />
            <Typography variant="h1" noSpacing>
              Hi {publicProfile?.firstName}
            </Typography>
            <Typography className={styles.email} variant="body" noSpacing>
              {lastSession.email}
            </Typography>
          </div>
        ) : null}
        {errors?.root?.server && (
          <Errors
            type={errors?.root?.server?.type as string}
            message={errors?.root?.server?.message}
          />
        )}
        <Input
          autoFocus={!isDeeplinkOverlayActive && !shouldUseLastSession}
          label={`Email`}
          errorMessage={errors?.email?.message}
          type={shouldUseLastSession ? "hidden" : "email"}
          disabled={isSubmitting || isBiometricProcessing}
          {...register("email")}
        />
        <PasswordInput
          autoFocus={!isDeeplinkOverlayActive && !!shouldUseLastSession}
          id={`password`}
          label={`Password`}
          errorMessage={errors?.password?.message}
          disabled={isSubmitting || isBiometricProcessing}
          {...register("password")}
        />
        <Button
          loading={isSubmitting || isBiometricProcessing}
          disabled={isSubmitting || isBiometricProcessing}
          type={`submit`}
        >
          Continue
        </Button>
      </Form>
      <div className={styles.links}>
        <Link to={`/forgot-password`}>Forgot Password?</Link>
        {shouldUseLastSession ? (
          <Typography component={`span`} variant="body" noSpacing>
            <Button
              variant={"link"}
              onClick={() => {
                reset();
                onIgnoreLastSession?.();
              }}
            >
              Not {publicProfile?.firstName}?
            </Button>
          </Typography>
        ) : (
          <Typography component={`span`} variant="body" noSpacing>
            No Account?{" "}
            <Button
              variant={"link"}
              onClick={() => {
                setModal("signUp");
                telemetry.signUpClicked({
                  component: "login_modal",
                });
              }}
            >
              Sign up
            </Button>
          </Typography>
        )}
      </div>
    </>
  );
};

export { SignInForm };
